/**
 * Deleting an event from its detail page.
 *
 * The service (AWSECEventHub `EventService#delete`, CR-319387680) soft-deletes and refuses with a
 * ConflictException while any campaign node references the event, so that no node is left pointing
 * at an event every read now hides. The product rule on top of that is "delete the child events
 * first": an event with anything beneath it in its hierarchy cannot be deleted, and the page says
 * so instead of offering a button that fails. "Anything" is any node under it — a child event or
 * an external SFDC campaign — because DeleteCampaignNode refuses a node with children as well, so
 * either one blocks.
 *
 * An event that is in a hierarchy but has nothing beneath it (typically a child event, a leaf
 * under its parent) is deletable: its own node is removed first (DeleteCampaignNode), then the
 * event (DeleteEvent). Two calls, not one transaction. If DeleteEvent then fails, the node is put
 * back under the same parent so the event is not silently dropped out of its hierarchy; if even
 * that fails, the error says the event is now outside its hierarchy so the marketer can re-add it
 * on Edit Event.
 */

import type { EventCalendarClient } from '../client/EventCalendarClient';
import type { HierarchyNode } from '../components/CampaignHierarchy/campaignHierarchy';

/**
 * Why Delete cannot be offered right now, given the event's tree as useEventHierarchy read it
 * (`nodes`): undefined while it loads, null if it could not be read, else the nodes beneath the
 * event. Null when nothing blocks. Fails closed: an unread hierarchy may well have children.
 */
export function deleteBlocker(nodes: readonly HierarchyNode[] | null | undefined): string | null {
  if (nodes === undefined) return 'Checking the event hierarchy…';
  if (nodes === null) {
    return 'The event hierarchy could not be read, so it is not known whether this event has child events. Reload the page and try again.';
  }
  if (nodes.length === 0) return null;
  const events = nodes.filter((n) => n.eventId).length;
  const campaigns = nodes.length - events;
  const what = [
    events ? `${events} child event${events === 1 ? '' : 's'}` : null,
    campaigns ? `${campaigns} SFDC campaign${campaigns === 1 ? '' : 's'}` : null,
  ]
    .filter(Boolean)
    .join(' and ');
  return `This event has ${what} beneath it in its hierarchy. Delete the child events (and remove the campaigns, on Edit Event) first.`;
}

/**
 * DeleteEvent failed after the event's node was removed, and the node could not be put back.
 * `parentNodeId` is where it was: only a node that had a parent has anything to be re-added under —
 * a childless root carried no hierarchy.
 */
export class EventLeftOutOfHierarchyError extends Error {
  constructor(
    readonly deleteError: unknown,
    readonly parentNodeId?: string,
  ) {
    super(messageOf(deleteError));
    this.name = 'EventLeftOutOfHierarchyError';
  }
}

/**
 * Removes the event's hierarchy node, if it has one, then deletes the event (see the header).
 * Rejects with DeleteEvent's or DeleteCampaignNode's own error, or with
 * {@link EventLeftOutOfHierarchyError} when the delete failed and the node could not be restored.
 */
export async function deleteEventAndNode(
  client: Pick<EventCalendarClient, 'deleteEvent' | 'campaignHierarchy'>,
  eventId: string,
): Promise<void> {
  const api = client.campaignHierarchy();
  // Looked up fresh rather than taken from the page: the parent is needed to put it back.
  const node = await api.findEventNode(eventId);
  if (node?.nodeId) await api.remove(node.nodeId);
  try {
    await client.deleteEvent(eventId);
  } catch (e: unknown) {
    if (node?.nodeId) {
      try {
        await api.createEventNode(eventId, node.parentNodeId);
      } catch {
        throw new EventLeftOutOfHierarchyError(e, node.parentNodeId);
      }
    }
    throw e;
  }
}

/**
 * A failed delete, as the page says it. By error type, not the service's message: a 403's message
 * would name the authorization rule (CWE-209), and a 409 is either "still in a hierarchy" (a child
 * was added meanwhile) or "changed meanwhile" — both are "reload and look again".
 */
export function deleteErrorMessage(e: unknown): string {
  if (e instanceof EventLeftOutOfHierarchyError) {
    const base = deleteErrorMessage(e.deleteError);
    // The removed node is a leaf, and a hierarchy is edited from above: on the PARENT's Edit Event
    // (as utils/childEvent's placement failure says), not this event's.
    return e.parentNodeId
      ? `${base} It was also taken out of its campaign hierarchy and could not be put back; add it again under Event Hierarchy on its parent event's Edit Event.`
      : base;
  }
  switch ((e as { name?: string } | null)?.name) {
    case 'ConflictException':
      return 'This event could not be deleted: something was added beneath it in its hierarchy, or it was changed meanwhile. Reload the page and try again.';
    case 'AccessDeniedException':
      return 'You cannot delete this event. An admin can delete any event; a marketer only a draft they created.';
    case 'ResourceNotFoundException':
      return 'This event no longer exists — it may already have been deleted.';
    default:
      return messageOf(e);
  }
}

function messageOf(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
